import { linkPath } from 'common/constants';
import { createCategoryKey } from 'common/utils/categoryUtils';
import { ShepherdStepDefinition } from './types';
import NotificationDrop from './vignettes/NotificationDrop';
import ScanMini from './vignettes/ScanMini';
import ShareHandoff from './vignettes/ShareHandoff';
import TypewriterSearch from './vignettes/TypewriterSearch';
import FinaleBurst from './vignettes/FinaleBurst';

const onDashboard = () => ({ path: linkPath.dashboard });

export const SHEPHERD_STEPS: ShepherdStepDefinition[] = [
  {
    id: 'welcome',
    chip: 'WELCOME',
    text: 'Would you like a quick 60-second tour of Doqit?',
    subtext: 'You can replay this tour anytime in Settings.',
    target: null,
    route: onDashboard,
    nextLabel: 'Start',
  },
  {
    id: 'dashboard-grid',
    chip: 'HOME',
    text: 'Everything in one place, organised into categories.',
    target: ['[data-tour="dashboard-grid"]'],
    pad: 6,
    radius: 20,
    route: onDashboard,
  },
  {
    id: 'hidden-categories',
    chip: 'HIDDEN, NOT GONE',
    text: 'We’ve hidden a few categories to keep things simple.',
    subtext: 'Turn them back on anytime in Settings > Manage Categories, or create your own.',
    target: ['[data-tour="ghost-tiles"]', '[data-tour="create-category"]'],
    pad: 6,
    radius: 20,
    route: onDashboard,
    condition: (ctx) => ctx.hiddenCategories.length > 0,
  },
  {
    id: 'your-category-pointer',
    chip: 'CATEGORIES',
    text: 'The doq you just added is now in its category. Tap Open it to take a look.',
    target: ['[data-tour="first-doq-tile"]'],
    pad: 6,
    radius: 18,
    route: onDashboard,
    condition: (ctx) => !!ctx.firstDoqCategory,
    nextLabel: 'Open it',
  },
  {
    id: 'category-doqs',
    chip: 'DOQ',
    // No "Open it" promise here: the tour's next button only advances the
    // step (the tour has no per-document context to actually open a doq,
    // and detouring into doc-detail mid-tour would strand the next steps) —
    // the old 'Open it' label made the advance feel like a broken deep link.
    // Lands straight after the dashboard's "Open it", so it reads as what
    // that tap opened, and the tour waits here until Next.
    text: 'Here’s the doq you just added, in its category. Tap it any time to see all its details.',
    target: ['[data-tour="category-doqs"]'],
    pad: 6,
    radius: 18,
    condition: (ctx) => !!ctx.firstDoqCategory,
    route: (ctx) => {
      const cat = ctx.firstDoqCategory;
      if (!cat) return null;
      return {
        path: `${linkPath.dashboardCategory}/${cat.id}`,
        // Same state shape Grid.tsx passes so ViewCategory renders normally.
        state: {
          ...cat,
          categoryKey: createCategoryKey(cat.id, cat.is_custom || false),
          sharedOnly: false,
        },
      };
    },
  },
  {
    id: 'category-groups',
    chip: 'ORGANISE',
    text: 'Keep related doqs together - for example, by person, account, vehicle or property.',
    target: ['[data-tour="category-groups"]'],
    pad: 6,
    radius: 18,
    condition: (ctx) => !!ctx.firstDoqCategory && ctx.firstDoqCategoryGroupCapable,
    // Already on the category page from the previous step - no hop needed.
    route: () => null,
  },
  {
    id: 'add-doq',
    chip: 'ADD A DOQ',
    text: 'Snap a photo or upload something important. Doqit reads it for you.',
    target: ['[data-tour="add-doc"]'],
    pad: 10,
    radius: 100,
    route: onDashboard,
    vignette: ScanMini,
  },
  {
    id: 'reminders',
    chip: 'REMINDERS',
    text: 'Set reminders, and Doqit will let you know when something needs your attention.',
    target: ['[data-tour="reminders-card"]', '[data-tour="nav-reminders"]'],
    pad: 6,
    radius: 18,
    route: onDashboard,
    vignette: NotificationDrop,
  },
  {
    id: 'search',
    chip: 'SEARCH',
    text: 'Find the doq you need in seconds.',
    target: ['[data-tour="search-pill"]'],
    pad: 5,
    radius: 100,
    route: onDashboard,
    vignette: TypewriterSearch,
  },
  {
    // Zero-document tour only (ZERO_DOC_STEP_IDS). Excluded from the
    // with-docs tour by its condition, so that tour is unchanged.
    id: 'quick-save',
    chip: 'QUICK SAVE',
    text: 'Save from any app.',
    subtext: 'Tap Share in Mail, Photos or Files, then Doqit. It files itself.',
    target: null,
    route: onDashboard,
    condition: (ctx) => !ctx.hasDocs,
  },
  {
    id: 'sharing-pointer',
    chip: 'SHARING',
    text: 'Share doqs securely with the people you trust.',
    target: ['[data-tour="nav-sharing"]'],
    pad: 8,
    radius: 100,
    route: onDashboard,
    // Desktop has no visible sharing nav entry; the 'sharing' step still
    // hops to /sharing-tab directly, which renders fine on desktop.
    condition: (ctx) => !ctx.isDesktop,
    nextLabel: 'See Sharing',
  },
  {
    id: 'sharing',
    chip: 'SHARING',
    text: 'Share a whole category or a single doq - it’s up to you.',
    target: ['[data-tour="sharing-header"]'],
    pad: 6,
    radius: 14,
    route: () => ({ path: linkPath.sharingTab }),
    vignette: ShareHandoff,
  },
  {
    id: 'account-pointer',
    chip: 'ACCOUNT',
    text: 'Tap your name to access settings, notifications, sharing and help.',
    target: ['[data-tour="avatar"]'],
    pad: 8,
    radius: 100,
    // The avatar exists in the sharing tab's header too, so no bounce back
    // to the dashboard is needed here.
    route: () => null,
  },
  {
    id: 'account',
    chip: 'ACCOUNT',
    text: 'Manage your account, get help and replay this tour - all in one place.',
    target: ['[data-tour="account-pills"]'],
    pad: 6,
    radius: 18,
    route: () => ({ path: linkPath.menuBar }),
  },
  {
    id: 'replay',
    chip: 'REPLAY ANYTIME',
    text: 'Replay this tour anytime from your Account.',
    target: ['[data-tour="replay-tour"]'],
    pad: 6,
    radius: 100,
    route: () => ({ path: linkPath.menuBar }),
  },
  {
    id: 'finale',
    chip: 'ALL SET',
    text: 'Add your next doq and let Doqit do the remembering.',
    target: null,
    route: onDashboard,
    nextLabel: 'Start',
    hideSkip: true,
    vignette: FinaleBurst,
  },
];

/** Step order for a user with no documents yet: no category phase, and a
 * Quick Save card in its place. */
export const ZERO_DOC_STEP_IDS = ['add-doq', 'reminders', 'search', 'quick-save', 'dashboard-grid', 'finale'];

/** The steps a tour walks, in order, before conditions are applied. */
export const getShepherdStepOrder = (hasDocs: boolean): ShepherdStepDefinition[] => (
  hasDocs
    ? SHEPHERD_STEPS
    : ZERO_DOC_STEP_IDS
      .map((id) => SHEPHERD_STEPS.find((s) => s.id === id))
      .filter((s): s is ShepherdStepDefinition => !!s)
);
